/** 4.10 Input (h 56, body-l, leading icon) for sign-in and code entry; Field (h 50, body) for forms. */
import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactElement, type TextareaHTMLAttributes } from "react";
import { Icon } from "./Icon";

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label: string;
  icon?: string;
  /** Error sentence: says what to do. Turns the ring alert-text. */
  error?: string | null;
  help?: string;
  secure?: boolean;
  /** `field` is the 50 px form variant. */
  variant?: "input" | "field";
  hideLabel?: boolean;
}
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input({ label, icon, error, help, secure = false, variant = "input", hideLabel = false, className = "", id, ...rest }, ref): ReactElement {
  const auto = useId(); const inputId = id ?? auto;
  const [shown, setShown] = useState(false);
  const type = secure ? (shown ? "text" : "password") : rest.type ?? "text";
  return (
    <div className={`inp ${error ? "inp--error" : ""} ${className}`}>
      <label htmlFor={inputId} className={`inp__label ${hideLabel ? "visually-hidden" : ""}`}>{label}</label>
      <div className={`inp__box ${variant === "field" ? "inp__box--field" : ""} ${icon ? "" : "inp__box--noicon"} ${secure ? "inp__box--secure" : ""}`}>
        {icon && <span className="inp__icon"><Icon name={icon} size={22} /></span>}
        <input ref={ref} id={inputId} className="inp__control" {...rest} type={type} aria-invalid={error ? true : undefined} aria-describedby={error ? `${inputId}-err` : help ? `${inputId}-help` : undefined} />
        {secure && <button type="button" className="inp__eye" aria-label={shown ? "Hide password" : "Show password"} aria-pressed={shown} onClick={() => setShown((s) => !s)}><Icon name={shown ? "eye" : "eye-off"} size={22} /></button>}
      </div>
      {error ? <p className="inp__msg" id={`${inputId}-err`} role="alert">{error}</p> : help ? <p className="inp__help" id={`${inputId}-help`}>{help}</p> : null}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> { label: string; error?: string | null; help?: string; hideLabel?: boolean }
export function TextArea({ label, error, help, hideLabel = false, className = "", id, ...rest }: TextareaProps): ReactElement {
  const auto = useId(); const inputId = id ?? auto;
  return (
    <div className={`inp ${error ? "inp--error" : ""} ${className}`}>
      <label htmlFor={inputId} className={`inp__label ${hideLabel ? "visually-hidden" : ""}`}>{label}</label>
      <div className="inp__box inp__box--field inp__box--multiline inp__box--noicon">
        <textarea id={inputId} className="inp__control" rows={3} {...rest} aria-invalid={error ? true : undefined} />
      </div>
      {error ? <p className="inp__msg" role="alert">{error}</p> : help ? <p className="inp__help">{help}</p> : null}
    </div>
  );
}
